import axios from "axios";
import { clientFetcher } from "./client-fetcher";

import type {
  RoleplayAnalysis,
  RoleplayCitation,
  RoleplayScenarioSlug,
  RoleplaySession,
  RoleplaySubskillInsight,
  WireCitation,
  WireSubskillInsight,
} from "@workspace/types";

/** `GET /training-sessions/analyzed` — the wire shape, snake_case as usual. */
interface WireRoleplayScenario {
  slug: RoleplayScenarioSlug;
  name: string;
  prospect_name?: string | null;
  company_name?: string | null;
}

interface WireRoleplayListItem {
  id: string;
  scenario: WireRoleplayScenario;
  score: number;
  completed_at: string;
}

const isNotFound = (error: unknown): boolean =>
  axios.isAxiosError(error) && error.response?.status === 404;

const toEpoch = (iso: string | null | undefined): number | null => {
  if (!iso) return null;
  const ms = Date.parse(iso);
  return Number.isNaN(ms) ? null : ms;
};

const mapRoleplay = (be: WireRoleplayListItem): RoleplaySession => ({
  id: be.id,
  scenarioSlug: be.scenario.slug,
  scenarioName: be.scenario.name,
  prospectName: be.scenario.prospect_name || null,
  companyName: be.scenario.company_name || null,
  score: be.score,
  completedAt: toEpoch(be.completed_at),
});

/**
 * Finished roleplays with feedback attached. Workspace scoping rides on the
 * `X-Workspace-Id` header, same as everything else.
 */
export const listAnalyzedRoleplays = async (): Promise<RoleplaySession[]> => {
  const { data } = await clientFetcher.get<{ items: WireRoleplayListItem[] }>(
    "/training-sessions/analyzed",
  );
  return (data.items ?? []).map(mapRoleplay);
};

// ── Analysis ───────────────────────────────────────────────

interface WireRoleplayAnalysis {
  id: string;
  scenario: WireRoleplayScenario;
  score: number;
  completed_at: string;
  subskill_insights?: WireSubskillInsight[] | null;
}

const mapCitation = (be: WireCitation | null | undefined): RoleplayCitation =>
  be
    ? { prospectQuote: be.prospect_quote, repQuote: be.rep_quote }
    : { prospectQuote: "", repQuote: "" };

export const mapSubskillInsight = (
  be: WireSubskillInsight,
): RoleplaySubskillInsight => ({
  subskillSlug: be.subskill_slug,
  subskillName: be.subskill_name,
  insight: be.insight,
  score: be.score.score,
  scoreText: be.score.text,
  roleplayCitation: mapCitation(be.roleplay_citations),
  realCallCitation: be.real_call_citations
    ? mapCitation(be.real_call_citations)
    : null,
  suggestion: be.suggestion,
  resource: be.resource,
});

const mapAnalysis = (be: WireRoleplayAnalysis): RoleplayAnalysis => ({
  id: be.id,
  scenarioSlug: be.scenario.slug,
  scenarioName: be.scenario.name,
  score: be.score,
  completedAt: toEpoch(be.completed_at),
  insights: (be.subskill_insights ?? []).map(mapSubskillInsight),
});

/** `422` from `/analysis`: the session will never be scored. */
interface WireRoleplayTooShort {
  error_code: "roleplay_too_short";
  detail: string;
  scenario_slug: RoleplayScenarioSlug;
}

/** The session ended before there was enough of it to score. */
export class RoleplayTooShortError extends Error {
  readonly scenarioSlug: RoleplayScenarioSlug;

  constructor(scenarioSlug: RoleplayScenarioSlug, detail: string) {
    super(detail);
    this.name = "RoleplayTooShortError";
    this.scenarioSlug = scenarioSlug;
  }
}

const asTooShort = (error: unknown): RoleplayTooShortError | null => {
  if (!axios.isAxiosError<WireRoleplayTooShort>(error)) return null;
  const body = error.response?.data;
  return body?.error_code === "roleplay_too_short"
    ? new RoleplayTooShortError(body.scenario_slug, body.detail)
    : null;
};

/** Coaching takes a while after the call ends, so the rep waits this long. */
const ANALYSIS_POLL_MS = 5000;
const ANALYSIS_ATTEMPTS = 60;

const sleep = (ms: number) => new Promise((done) => setTimeout(done, ms));

/**
 * The feedback for one finished roleplay, as it stands right now. Returns null
 * while the analysis is still being written — a 404 and an empty insight list
 * both mean the same thing. A session too short to score throws
 * `RoleplayTooShortError`.
 */
export const getRoleplayAnalysis = async (
  sessionId: string,
): Promise<RoleplayAnalysis | null> => {
  try {
    const { data } = await clientFetcher.get<WireRoleplayAnalysis>(
      `/training-sessions/${sessionId}/analysis`,
    );
    const analysis = mapAnalysis(data);
    return analysis.insights.length > 0 ? analysis : null;
  } catch (error) {
    // Not written yet; anything else is a real failure and surfaces as one.
    if (isNotFound(error)) return null;
    throw asTooShort(error) ?? error;
  }
};

/**
 * The same feedback, waited for. Straight after a session the analysis does
 * not exist yet, so the results screen sits here until it lands. A session too
 * short to score fails on the first attempt instead of waiting the polls out.
 */
export const awaitRoleplayAnalysis = async (
  sessionId: string,
): Promise<RoleplayAnalysis> => {
  for (let attempt = 1; attempt <= ANALYSIS_ATTEMPTS; attempt++) {
    const analysis = await getRoleplayAnalysis(sessionId);
    if (analysis) return analysis;
    if (attempt < ANALYSIS_ATTEMPTS) await sleep(ANALYSIS_POLL_MS);
  }

  throw new Error("Roleplay analysis is not available");
};
